<template>

  <div class="ue-expenses-edit">
    <el-card>
      <div class="header">
        <h2>000 - Износ (Год: {{ dto.trancheYear }})</h2>
        <div>
          <el-button @click="goBack">Назад</el-button>
          <el-button type="primary" @click="submitForm()" :loading="isLoading" v-if="dto.status !== 'APPROVED' && hasEditPermission()">Сохранить</el-button>
        </div>
      </div>

      <form>
        <div class="add-item-section" v-if="dto.status !== 'APPROVED' && hasEditPermission() && availableCategories.length > 0">
          <div class="row col-md-9">
            <div class="el-input-group">
              <label class="form-label col-md-3">Добавить категорию</label>
              <div class="col-md-6">
                <select v-model="selectedCategory" class="form-select">
                  <option :value="null" disabled>Выберите категорию</option>
                  <option v-for="cat in availableCategories" :key="cat.value" :value="cat.value">
                    {{ cat.title }}
                  </option>
                </select>
              </div>
              <div class="col-md-3">
                <el-button type="success" @click="addItem()" :disabled="!selectedCategory">
                  Добавить
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="table-container" @paste="handlePaste" @keydown="handleKeydown">
          <table class="grid-table">
            <thead>
            <tr>
              <th class="title-cell header-success" rowspan="2">Наименование</th>
              <th class="depreciation-cost-cell header-success" rowspan="2">стоимость для расчета амортизации</th>
              <th class="wear-rate-cell header-success" rowspan="2">средняя норма износа в год</th>
              <th class="total-cell header-success" rowspan="2">ВСЕГО</th>
              <th class="header-success" colspan="12">в т.ч. по месяцам</th>
              <th class="action-col" rowspan="2" v-if="dto.status !== 'APPROVED' && hasEditPermission()"></th>
            </tr>
            <tr>
              <th v-for="name in monthNames" :key="name" class="month-cell header-success">{{ name }}</th>
            </tr>
            </thead>
            <tbody>
            <tr class="total-row calculated-row readonly-text">
              <td>ВСЕГО</td>
              <td>{{ formatNumber(totals.depreciationCost) }}</td>
              <td>{{ formatPercent(calcTotalWearRate()) }}</td>
              <td>{{ formatNumber(totals.total) }}</td>
              <td v-for="(field, idx) in monthFields" :key="'total-'+idx">{{ formatNumber(totals[field]) }}</td>
              <td v-if="dto.status !== 'APPROVED' && hasEditPermission()"></td>
            </tr>
            <template v-for="(item, rowIndex) in dto.items" :key="item.uuid || rowIndex">
              <tr>
                <td>{{ item.serviceName }}</td>
                <td class="expense-sum-edit">
                  <input
                      :value="getCellValue(item, 'depreciationCost')"
                      @focus="onFocus($event, item, 'depreciationCost')"
                      @blur="onBlur($event, item, 'depreciationCost')"
                      @input="onInput($event, item, 'depreciationCost')"
                      @keypress="onKeypress"
                      :data-row="rowIndex"
                      :data-col="0"
                      class="cell-input"
                      type="text"
                  />
                </td>
                <td class="calculated-cell">{{ formatPercent(calcWearRate(item)) }}</td>
                <td class="calculated-cell">{{ formatNumber(getItemTotal(item)) }}</td>
                <td v-for="(field, colIndex) in monthFields" :key="field" class="expense-sum-edit">
                  <input
                      :value="getCellValue(item, field)"
                      @focus="onFocus($event, item, field)"
                      @blur="onBlur($event, item, field)"
                      @input="onInput($event, item, field)"
                      @keypress="onKeypress"
                      :data-row="rowIndex"
                      :data-col="colIndex + 3"
                      class="cell-input"
                      type="text"
                  />
                </td>
                <td class="action-col" v-if="dto.status !== 'APPROVED' && hasEditPermission()">
                  <el-button type="danger" size="small" @click="removeItem(rowIndex, item)">
                    &times;
                  </el-button>
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </div>
      </form>
    </el-card>
  </div>

  <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
</template>

<script>

import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import {showErrorNotify, showSuccessNotify} from "@/utils/notificationUtils";
import delegationService from "@/services/delegationService";
import ueExpensesCommonCrudService from "@/services/expenses/ueExpensesCommonCrudService";
import {handleGridKeydown} from "@/utils/gridKeyboardNav";

const MONTH_FIELDS = ['month1','month2','month3','month4','month5','month6','month7','month8','month9','month10','month11','month12'];
const ALL_PASTE_FIELDS = ['depreciationCost', 'SKIP', 'SKIP', ...MONTH_FIELDS];

const ALL_CATEGORIES = [
  { value: 'BUILDINGS', title: 'Здания, сооружения' },
  { value: 'TRANSPORT_VEHICLES', title: 'Транспортные средства, спецтехника' },
  { value: 'SPECIAL_EQUIPMENT', title: 'Спецоборудование, оборудование' },
  { value: 'TOOLS_INVENTORY', title: 'Инструменты, производственный и хозяйственный инвентарь' },
  { value: 'FURNITURE', title: 'Мебель, офисная, лабораторная, общежитий' },
  { value: 'COMMUNICATION_MEANS', title: 'Средства связи' },
  { value: 'OFFICE_COMPUTERS', title: 'Оргтехника, компьютеры' },
  { value: 'HOUSEHOLD_APPLIANCES', title: 'Бытовая техника' },
  { value: 'SPORTS_INVENTORY', title: 'Спортивный инвентарь' },
  { value: 'OTHER_FIXED_ASSETS', title: 'Прочие основные средства' },
  { value: 'INTANGIBLE_ASSETS', title: 'Нематериальные активы' },
  { value: 'BIOLOGICAL_ASSETS', title: 'Биологические активы' },
];

export default {
  components: {
    VueLoading
  },
  computed: {
    uuid() {
      return this.$route.params.uuid;
    },
    availableCategories() {
      return ALL_CATEGORIES.filter(c => !this.dto.items.some(i => i.categoryType === c.value));
    }
  },
  data() {
    return {
      isLoading: false,
      expenseType: 'depreciation',
      dto: {
        uuid: null,
        trancheYear: null,
        universityId: null,
        status: null,
        items: []
      },
      selectedCategory: null,
      monthFields: MONTH_FIELDS,
      totals: {
        depreciationCost: 0,
        month1: 0, month2: 0, month3: 0, month4: 0, month5: 0, month6: 0,
        month7: 0, month8: 0, month9: 0, month10: 0, month11: 0, month12: 0,
        total: 0
      },
      monthNames: [
        'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
        'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
      ],
      focusedCell: null,
      editingValue: '',
    };
  },
  methods: {
    init() {
      this.findById(this.uuid);
    },

    async findById(uuid) {
      this.isLoading = true;
      this.dto = await ueExpensesCommonCrudService.findById(this.expenseType, uuid);
      if (!this.dto.items) {
        this.dto.items = [];
      }
      this.recalculate();
      this.isLoading = false;
    },

    async submitForm() {
      try {
        if (!this.hasEditPermission()) return;
        this.isLoading = true;
        for (const item of this.dto.items) {
          const rate = this.calcWearRate(item);
          item.averageWearRate = rate === 0 ? null : Math.round(rate * 100) / 100;
        }
        this.dto = await ueExpensesCommonCrudService.save(this.expenseType, this.dto);
        if (!this.dto.items) {
          this.dto.items = [];
        }
        this.recalculate();
        showSuccessNotify("Успешно сохранено!");
      } catch (exc) {
        showErrorNotify("Возникла ошибка при сохранении");
      } finally {
        this.isLoading = false;
      }
    },

    addItem() {
      if (!this.selectedCategory) return;
      const cat = ALL_CATEGORIES.find(c => c.value === this.selectedCategory);
      if (!cat) return;
      this.dto.items.push({
        uuid: null,
        depreciationId: this.dto.uuid,
        serviceName: cat.title,
        categoryType: cat.value,
        depreciationCost: null,
        averageWearRate: null,
        month1: null, month2: null, month3: null, month4: null,
        month5: null, month6: null, month7: null, month8: null,
        month9: null, month10: null, month11: null, month12: null
      });
      this.selectedCategory = null;
      this.recalculate();
    },

    async removeItem(index, item) {
      if (item.uuid) {
        try {
          await ueExpensesCommonCrudService.deleteCategory(this.expenseType, this.dto.uuid, item.uuid);
          showSuccessNotify('Удалено');
        } catch (exc) {
          showErrorNotify('Ошибка при удалении');
          return;
        }
      }
      this.dto.items.splice(index, 1);
      this.recalculate();
    },

    parseNumber(str) {
      if (str === null || str === undefined || str === '') return 0;
      const cleaned = String(str).replace(/\s/g, '').replace(',', '.');
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    },

    formatNumber(val) {
      const num = typeof val === 'number' ? val : this.parseNumber(val);
      if (num === 0) return '';
      return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    getCellValue(item, field) {
      if (this.focusedCell && this.focusedCell.item === item && this.focusedCell.field === field) {
        return this.editingValue;
      }
      return this.getDisplayValue(item[field]);
    },

    getDisplayValue(val) {
      const num = this.parseNumber(val);
      if (num === 0) return '';
      return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    },

    onFocus(event, item, field) {
      this.focusedCell = { item, field };
      const num = this.parseNumber(item[field]);
      this.editingValue = num === 0 ? '' : String(num).replace('.', ',');
      event.target.value = this.editingValue;
    },

    onBlur(event, item, field) {
      this.focusedCell = null;
      const num = this.parseNumber(event.target.value);
      item[field] = num === 0 ? null : this.roundTo2(num);
      event.target.value = this.getDisplayValue(item[field]);
      this.recalculate();
    },

    onInput(event, item, field) {
      let sanitized = event.target.value.replace('.', ',').replace(/[^\d,]/g, '');
      const parts = sanitized.split(',');
      if (parts.length > 2) {
        sanitized = parts[0] + ',' + parts.slice(1).join('');
      }
      if (sanitized !== event.target.value) {
        event.target.value = sanitized;
      }
      this.editingValue = sanitized;
      const num = this.parseNumber(sanitized);
      item[field] = num === 0 ? null : this.roundTo2(num);
      this.recalculate();
    },

    onKeypress(event) {
      const char = event.key;
      if (char.length > 1) return;
      if (/[0-9]/.test(char)) return;
      if ((char === ',' || char === '.') && !event.target.value.includes(',')) return;
      event.preventDefault();
    },

    roundTo2(num) {
      return Math.round(num * 100) / 100;
    },

    getItemTotal(item) {
      let sum = 0;
      for (const field of MONTH_FIELDS) {
        sum += this.parseNumber(item[field]);
      }
      return sum;
    },

    calcWearRate(item) {
      const cost = this.parseNumber(item.depreciationCost);
      const total = this.getItemTotal(item);
      if (cost === 0) return 0;
      return (total / cost) * 100;
    },

    calcTotalWearRate() {
      const cost = this.totals.depreciationCost;
      const total = this.totals.total;
      if (cost === 0) return 0;
      return (total / cost) * 100;
    },

    formatPercent(val) {
      return val.toFixed(2).replace('.', ',') + '%';
    },

    recalculate() {
      if (!this.dto.items || this.dto.items.length === 0) {
        const empty = {};
        empty.depreciationCost = 0;
        for (const field of MONTH_FIELDS) { empty[field] = 0; }
        empty.total = 0;
        this.totals = empty;
        return;
      }

      const totals = {};
      totals.depreciationCost = 0;
      for (const field of MONTH_FIELDS) { totals[field] = 0; }
      totals.total = 0;

      for (const item of this.dto.items) {
        totals.depreciationCost += this.parseNumber(item.depreciationCost);
        for (const field of MONTH_FIELDS) {
          const val = this.parseNumber(item[field]);
          totals[field] += val;
          totals.total += val;
        }
      }

      this.totals = totals;
    },

    handleKeydown(event) {
      handleGridKeydown(event, this.$el);
    },

    handlePaste(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT') return;

      const startRow = parseInt(target.dataset.row);
      const startCol = parseInt(target.dataset.col);
      if (isNaN(startRow) || isNaN(startCol)) return;

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (!text) return;

      const rows = text.split(/\r?\n/).filter(r => r.trim() !== '');
      if (rows.length <= 1 && !rows[0].includes('\t')) return;

      event.preventDefault();

      for (let r = 0; r < rows.length; r++) {
        const cols = rows[r].split('\t');
        const itemIdx = startRow + r;
        if (itemIdx >= this.dto.items.length) break;

        for (let c = 0; c < cols.length; c++) {
          const colIdx = startCol + c;
          if (colIdx >= ALL_PASTE_FIELDS.length) break;

          const field = ALL_PASTE_FIELDS[colIdx];
          if (field === 'SKIP') continue;
          const num = this.parseNumber(cols[c]);
          this.dto.items[itemIdx][field] = num === 0 ? null : this.roundTo2(num);
        }
      }

      this.recalculate();
      this.$forceUpdate();
    },

    goBack() {
      this.$router.go(-1);
    },
    hasEditPermission() {
      return this.isUniversityManager() && (this.dto.status === 'CREATED' || !this.dto.status);
    },
    isUniversityManager() {
      return delegationService.isUniversityManager();
    },
  },
  mounted() {
    this.init();
  }
};
</script>

<style scoped>
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.add-item-section { margin-bottom: 20px; }
.table-container { width: 100%; overflow-x: auto; }
.grid-table { width: 100%; border-collapse: collapse; min-width: 2800px; }
.grid-table th, .grid-table td { border: 1px solid #EBEEF5; padding: 8px; font-size: 12px; }
.grid-table th { background-color: #f5f7fa; text-align: center; font-weight: bold; position: sticky; top: 0; z-index: 10; }
.header-success { background-color: #d4edda !important; }
.title-cell { width: 180px; min-width: 180px; }
.depreciation-cost-cell { width: 140px; min-width: 140px; }
.wear-rate-cell { width: 120px; min-width: 120px; }
.meta-cell { width: 90px; min-width: 90px; }
.month-cell { width: 85px; min-width: 85px; }
.total-cell { width: 100px; min-width: 100px; }
.action-col { width: 50px; min-width: 50px; text-align: center; }
.name-cell { min-width: 180px; }
.calculated-cell { text-align: right; background-color: #f9f9f9; }
.calculated-row { background-color: #f0f9eb; font-weight: bold; }
.readonly-text { text-align: right; padding-right: 10px !important; }
.total-row td { font-weight: bold; text-align: right; }
.total-row td:first-child { text-align: left; }

.expense-sum-edit { padding: 2px !important; }

.cell-input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  text-align: right;
  font-size: 12px;
  padding: 4px 6px;
  box-sizing: border-box;
}

.cell-input.text-left {
  text-align: left;
}

.cell-input:focus {
  background-color: #fff;
  border: 1px solid #409eff;
  border-radius: 2px;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}
</style>
